/*
  Roleta da sorte — lógica do pop-up.

  Princípio central: o resultado é decidido ANTES do giro.
  A animação não sorteia nada, ela apenas revela um resultado já definido.

  Organização:
    Storage      → persistência por navegador (etapas: appeared / played / claimed)
    Analytics    → emissão de eventos (CustomEvent + dataLayer)
    Geometry     → matemática de ângulos e fatias
    WheelRenderer→ desenho da roda no canvas
    SpinEngine   → animação do giro e efeito sonoro
    PrizePicker  → sorteio ponderado do prêmio
    WheelPopup   → custom element que orquestra a interface
*/

(() => {
  'use strict';

  const POINTER_ANGLE = 270; // Ponteiro no topo. No canvas, 0° = leste e cresce no sentido horário.

  /* ============================================================
     Storage
     ============================================================ */

  class Storage {
    constructor(namespace, readOnly = false) {
      this.namespace = namespace;
      // No editor de temas não gravamos nada: testar a roleta ali não pode
      // "queimar" o giro do lojista na loja real, que usa o mesmo navegador.
      this.readOnly = readOnly;
    }

    key(name) {
      return `${this.namespace}:${name}`;
    }

    get(name) {
      try {
        return localStorage.getItem(this.key(name));
      } catch (error) {
        return null; // Modo privado ou storage indisponível.
      }
    }

    set(name, value) {
      if (this.readOnly) return;

      try {
        localStorage.setItem(this.key(name), String(value));
      } catch (error) {
        /* Seguimos sem persistir: a experiência não deve quebrar por causa disso. */
      }
    }

    has(name) {
      return this.get(name) === 'true';
    }

    clear() {
      ['appeared', 'played', 'claimed', 'closed', 'coupon', 'prize', 'email'].forEach((name) => {
        try {
          localStorage.removeItem(this.key(name));
        } catch (error) {
          /* idem */
        }
      });
    }
  }

  /* ============================================================
     Analytics
     ============================================================ */

  class Analytics {
    constructor(config) {
      this.config = config;
    }

    track(name, payload = {}) {
      const detail = { campaign: this.config.campaignId, ...payload };

      document.dispatchEvent(new CustomEvent(`wheel:${name}`, { detail, bubbles: true }));

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `wheel_${name}`, ...detail });

      if (typeof window.gtag === 'function') {
        window.gtag('event', `wheel_${name}`, detail);
      }
    }
  }

  /* ============================================================
     Geometry
     ============================================================ */

  const Geometry = {
    step(sliceCount) {
      return 360 / sliceCount;
    },

    /*
      Fração de onde parar dentro da fatia (0 = início, 1 = fim).
      Com probabilidade `edgeBias`, o ponto fica colado numa das divisórias:
      é o efeito "nossa, quase caiu na triste".
    */
    landingFraction(edgeBias, margin) {
      const min = margin;
      const max = 1 - margin;

      if (Math.random() < edgeBias) {
        const offset = min + Math.random() * 0.1;
        return Math.random() < 0.5 ? offset : 1 - offset;
      }

      return min + Math.random() * (max - min);
    },

    /*
      Rotação final (em graus, acumulada) para que o centro escolhido da fatia
      `index` termine sob o ponteiro, após `spins` voltas completas.
    */
    rotationFor(index, sliceCount, currentRotation, spins, edgeBias, margin) {
      const step = this.step(sliceCount);
      const target = index * step + step * this.landingFraction(edgeBias, margin);
      const desired = ((POINTER_ANGLE - target) % 360 + 360) % 360;
      const delta = ((desired - (currentRotation % 360)) % 360 + 360) % 360;

      return currentRotation + spins * 360 + delta;
    },

    /* Qual fatia está sob o ponteiro para uma dada rotação. Usado para o "tique". */
    sliceUnderPointer(rotation, sliceCount) {
      const step = this.step(sliceCount);
      const angle = ((POINTER_ANGLE - rotation) % 360 + 360) % 360;
      return Math.floor(angle / step);
    },
  };

  /* ============================================================
     WheelRenderer
     ============================================================ */

  class WheelRenderer {
    constructor(canvas, config) {
      this.canvas = canvas;
      this.config = config;
      this.context = canvas.getContext('2d');
      this.images = {};
    }

    get slices() {
      // Fatias alternadas: índices pares vencem, ímpares perdem.
      return Array.from({ length: this.config.sliceCount }, (_, index) => {
        const isWinner = index % 2 === 0;
        return {
          isWinner,
          key: isWinner ? 'win' : 'lose',
          symbol: isWinner ? this.config.winSymbol : this.config.loseSymbol,
          color: isWinner ? this.config.winColor : this.config.loseColor,
        };
      });
    }

    /*
      Carrega as artes das fatias, quando configuradas. Sem elas, o desenho cai
      no emoji do sistema — que muda de aparência conforme o aparelho.
    */
    loadImages() {
      const sources = { win: this.config.winImage, lose: this.config.loseImage };

      return Promise.all(
        Object.entries(sources).map(
          ([key, src]) =>
            new Promise((resolve) => {
              if (!src) return resolve();

              const image = new Image();
              image.crossOrigin = 'anonymous';
              image.onload = () => {
                this.images[key] = image;
                resolve();
              };
              image.onerror = resolve; // Falhou: seguimos com o emoji.
              image.src = src;
            })
        )
      );
    }

    draw() {
      const rect = this.canvas.getBoundingClientRect();
      if (rect.width === 0) return; // Ainda invisível: nada a desenhar.

      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const size = rect.width;

      this.canvas.width = Math.round(size * ratio);
      this.canvas.height = Math.round(size * ratio);

      const ctx = this.context;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, size, size);

      const center = size / 2;
      const rim = Math.max(4, size * 0.03);
      const radius = center - rim;
      const slices = this.slices;
      const step = (Math.PI * 2) / slices.length;

      slices.forEach((slice, index) => {
        const start = index * step;

        ctx.beginPath();
        ctx.moveTo(center, center);
        ctx.arc(center, center, radius, start, start + step);
        ctx.closePath();
        ctx.fillStyle = slice.color;
        ctx.fill();

        ctx.strokeStyle = this.config.separatorColor;
        ctx.lineWidth = Math.max(1, size * 0.004);
        ctx.stroke();

        // Símbolo da fatia na vertical: emoji rotacionado fica ilegível, e o
        // contraste imediato entre ganhar e perder é o que cria a tensão.
        const angle = start + step / 2;
        const distance = radius * 0.72;
        const x = center + Math.cos(angle) * distance;
        const y = center + Math.sin(angle) * distance;
        const art = this.images[slice.key];

        if (art) {
          const artSize = size * 0.13;
          ctx.drawImage(art, x - artSize / 2, y - artSize / 2, artSize, artSize);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = `${Math.round(size * 0.1)}px ${this.config.symbolFont}`;
          ctx.fillText(slice.symbol, x, y);
        }
      });

      // Aro externo.
      ctx.beginPath();
      ctx.arc(center, center, radius + rim / 2, 0, Math.PI * 2);
      ctx.strokeStyle = this.config.rimColor;
      ctx.lineWidth = rim;
      ctx.stroke();
    }
  }

  /* ============================================================
     SpinEngine
     ============================================================ */

  class SpinEngine {
    constructor(canvas, config) {
      this.canvas = canvas;
      this.config = config;
      this.rotation = 0;
      this.spinning = false;
      this.audioContext = null;
    }

    /* Curva de desaceleração estilo cassino (equivalente a um cubic-bezier). */
    static ease(t) {
      return 1 - Math.pow(1 - t, 3.4);
    }

    /*
      Navegadores só liberam áudio a partir de um gesto do usuário. Criamos e
      destravamos o contexto no clique em GIRAR; se esperarmos a animação, ele
      nasce suspenso e nenhum tique é ouvido.
    */
    primeAudio() {
      if (!this.config.sound || this.audioContext) return;

      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (this.audioContext.state === 'suspended') this.audioContext.resume();
      } catch (error) {
        this.config.sound = false; // Áudio indisponível: desliga e segue.
      }
    }

    playTick() {
      if (!this.config.sound || !this.audioContext) return;

      try {
        const ctx = this.audioContext;
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();

        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(1100, ctx.currentTime);
        gain.gain.setValueAtTime(0.05, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.045);

        oscillator.connect(gain).connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.05);
      } catch (error) {
        this.config.sound = false; // Áudio bloqueado: desliga e segue.
      }
    }

    spinTo(targetRotation) {
      return new Promise((resolve) => {
        if (this.spinning) return resolve();
        this.spinning = true;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const duration = reduceMotion ? 600 : this.config.spinDuration;
        const from = this.rotation;
        const distance = targetRotation - from;
        const start = performance.now();

        let lastSlice = Geometry.sliceUnderPointer(from, this.config.sliceCount);

        const frame = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const current = from + distance * SpinEngine.ease(progress);

          this.canvas.style.transform = `rotate(${current}deg)`;

          if (!reduceMotion) {
            const slice = Geometry.sliceUnderPointer(current, this.config.sliceCount);
            if (slice !== lastSlice) {
              lastSlice = slice;
              this.playTick();
            }
          }

          if (progress < 1) {
            requestAnimationFrame(frame);
          } else {
            this.rotation = targetRotation;
            this.spinning = false;
            resolve();
          }
        };

        requestAnimationFrame(frame);
      });
    }
  }

  /* ============================================================
     PrizePicker
     ============================================================ */

  const PrizePicker = {
    /* O resultado (ganhou/perdeu) é decidido aqui, antes de qualquer animação. */
    decideOutcome(winChance) {
      return Math.random() * 100 < winChance;
    },

    /* Sorteio ponderado entre os prêmios configurados. */
    pick(prizes) {
      const valid = (prizes || []).filter((prize) => prize && prize.label);
      if (valid.length === 0) return null;

      const total = valid.reduce((sum, prize) => sum + Math.max(0, Number(prize.weight) || 0), 0);
      if (total <= 0) return valid[0];

      let roll = Math.random() * total;
      for (const prize of valid) {
        roll -= Math.max(0, Number(prize.weight) || 0);
        if (roll <= 0) return prize;
      }

      return valid[valid.length - 1];
    },

    /* Índice de uma fatia coerente com o resultado já decidido. */
    sliceFor(won, sliceCount) {
      const candidates = [];
      for (let index = 0; index < sliceCount; index += 1) {
        if ((index % 2 === 0) === won) candidates.push(index);
      }
      return candidates[Math.floor(Math.random() * candidates.length)];
    },
  };

  /* ============================================================
     WheelPopup (custom element)
     ============================================================ */

  class WheelPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-wheel-config]').textContent);
      } catch (error) {
        // Sem config não há roleta, mas o erro precisa ser visível: falhar em
        // silêncio aqui custou horas de depuração.
        console.error('Roleta: configuração inválida.', error);
        return;
      }
      this.storage = new Storage(this.config.storageKey, this.config.designMode);
      this.analytics = new Analytics(this.config);
      this.canvas = this.querySelector('[data-wheel-canvas]');
      this.renderer = new WheelRenderer(this.canvas, this.config);
      this.engine = new SpinEngine(this.canvas, this.config);
      this.prize = null;
      this.lastFocused = null;
      this.contact = {};
      this.contactStage = 'email';

      this.bindEvents();
      this.scheduleAutoOpen();

      // As artes chegam de forma assíncrona: redesenhamos quando estiverem prontas.
      this.renderer.loadImages().then(() => {
        if (!this.hidden) this.renderer.draw();
      });
    }

    /* ---------- Interface ---------- */

    goToStep(name) {
      this.querySelectorAll('[data-wheel-step]').forEach((step) => {
        step.hidden = step.dataset.wheelStep !== name;
      });
    }

    open(source = 'auto') {
      if (!this.hidden) return;

      this.lastFocused = document.activeElement;
      this.hidden = false;
      document.body.style.overflow = 'hidden';

      // O canvas precisa estar visível para ter dimensões mensuráveis.
      this.renderer.draw();

      if (this.storage.has('claimed') && this.storage.get('coupon')) {
        this.restoreCoupon();
      } else if (this.config.emailStep === 'before' && !this.storage.get('email')) {
        this.openContactForm();
      } else {
        this.goToStep('intro');
      }

      this.querySelector('.wheel__card').focus();
      this.storage.set('appeared', 'true');
      this.analytics.track('open', { source });
    }

    close() {
      this.hidden = true;
      document.body.style.overflow = '';
      // Quem fecha no X não deve ser incomodado de novo. No editor a gravação
      // é ignorada (storage readOnly), então isso não atrapalha a edição.
      this.storage.set('closed', 'true');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
      this.analytics.track('close', { played: this.storage.has('played') });
    }

    /* ---------- Giro ---------- */

    async spin() {
      if (this.engine.spinning) return;

      const button = this.querySelector('[data-wheel-spin]');
      button.disabled = true;
      this.engine.primeAudio(); // Ainda dentro do gesto do clique.

      // 1. Decide o resultado ANTES de animar.
      const won = this.config.winChance >= 100 ? true : PrizePicker.decideOutcome(this.config.winChance);
      this.prize = won ? PrizePicker.pick(this.config.prizes) : null;

      if (won && !this.prize) {
        button.disabled = false;
        return; // Nenhum prêmio configurado: não há o que entregar.
      }

      this.analytics.track('spin', { outcome: won ? 'win' : 'lose' });

      // 2. Escolhe uma fatia coerente e calcula onde parar.
      const slice = PrizePicker.sliceFor(won, this.config.sliceCount);
      const rotation = Geometry.rotationFor(
        slice,
        this.config.sliceCount,
        this.engine.rotation,
        this.config.spins,
        this.config.edgeBias,
        this.config.sliceMargin
      );

      // 3. Anima até o resultado já decidido.
      await this.engine.spinTo(rotation);

      this.storage.set('played', 'true');
      this.showResult(won);
    }

    showResult(won) {
      if (!won) {
        this.goToStep('lose');
        this.analytics.track('lose');
        return;
      }

      this.querySelector('[data-wheel-prize-label]').textContent = this.prize.label;
      this.querySelector('[data-wheel-prize-desc]').textContent = this.prize.description || '';
      this.storage.set('prize', this.prize.label);

      this.goToStep('win');
      this.analytics.track('win', { prize: this.prize.label, coupon: this.prize.coupon });
    }

    /* ---------- Cupom ---------- */

    showCoupon() {
      const coupon = this.prize ? this.prize.coupon : this.storage.get('coupon');
      if (!coupon) return;

      this.querySelector('[data-wheel-code]').textContent = coupon;

      const redirect = encodeURIComponent(this.config.discountRedirect || '/');
      this.querySelector('[data-wheel-apply]').href = `/discount/${encodeURIComponent(coupon)}?redirect=${redirect}`;

      this.storage.set('claimed', 'true');
      this.storage.set('coupon', coupon);
      this.goToStep('coupon');
    }

    /* Visitante que já resgatou reabre o pop-up: mostramos o cupom dele de volta. */
    restoreCoupon() {
      const label = this.storage.get('prize');
      if (label) this.querySelector('[data-wheel-prize-label]').textContent = label;
      this.showCoupon();
      this.analytics.track('coupon_restore');
    }

    /* ---------- Contato ---------- */

    /*
      O formulário avança um campo por vez: e-mail e, se configurado, telefone.
      Só ao final os dados vão para a Shopify, numa única requisição.
    */
    async submitContact(form) {
      const errorEl = this.querySelector('[data-wheel-error]');
      const button = form.querySelector('[data-wheel-submit]');

      errorEl.hidden = true;

      if (this.contactStage === 'email') {
        const field = form.querySelector('[name="email"]');
        const email = field.value.trim();

        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          return this.showFieldError(errorEl, this.config.emailErrorText, field);
        }

        this.contact.email = email;
        this.analytics.track('email_submit');

        if (this.config.collectPhone) return this.goToPhoneField();
      } else {
        const field = form.querySelector('[name="phone"]');
        const phone = field.value.trim();

        // Aceita qualquer formatação; o que importa é ter DDD + número.
        if (phone.replace(/\D/g, '').length < 10) {
          return this.showFieldError(errorEl, this.config.phoneErrorText, field);
        }

        this.contact.phone = this.withCountryCode(phone);
        this.analytics.track('phone_submit');
      }

      await this.sendContact(button);

      // Antes do giro o formulário leva à roleta; depois do giro, ao cupom.
      if (this.config.emailStep === 'before') {
        this.goToStep('intro');
      } else {
        this.showCoupon();
      }
    }

    /* Sempre reabre o formulário no primeiro campo, com os textos originais. */
    openContactForm() {
      this.contactStage = 'email';

      this.querySelector('[data-wheel-field="email"]').hidden = false;
      this.querySelector('[data-wheel-field="phone"]').hidden = true;
      this.querySelector('[data-wheel-form-title]').textContent = this.config.formTitle;
      this.querySelector('[data-wheel-form-subtitle]').innerHTML = this.config.formSubtitle || '';
      this.querySelector('[data-wheel-submit]').textContent = this.config.formButton;
      this.querySelector('[data-wheel-error]').hidden = true;

      this.goToStep('form');
      this.querySelector('[data-wheel-field="email"] input').focus();
    }

    /* O prefixo do campo é apenas visual; ao salvar, guardamos o número completo. */
    withCountryCode(phone) {
      const code = (this.config.phonePrefix || '').match(/\d+/);
      return code ? `+${code[0]} ${phone}` : phone;
    }

    /* Formata o número no padrão brasileiro conforme o visitante digita. */
    formatPhone(value) {
      const digits = value.replace(/\D/g, '').slice(0, 11);
      if (digits.length === 0) return '';
      if (digits.length <= 2) return `(${digits}`;
      if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }

    showFieldError(errorEl, message, field) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      field.focus();
    }

    goToPhoneField() {
      this.contactStage = 'phone';

      this.querySelector('[data-wheel-field="email"]').hidden = true;

      const field = this.querySelector('[data-wheel-field="phone"]');
      field.hidden = false;

      this.querySelector('[data-wheel-form-title]').textContent = this.config.phoneTitle;
      this.querySelector('[data-wheel-form-subtitle]').innerHTML = this.config.phoneSubtitle || '';
      this.querySelector('[data-wheel-submit]').textContent = this.config.formButtonFinal;

      field.querySelector('input').focus();
    }

    async sendContact(button) {
      const body = new FormData();
      body.append('form_type', 'customer');
      body.append('utf8', '✓');
      body.append('contact[email]', this.contact.email);
      body.append('contact[tags]', this.config.customerTag);
      body.append('contact[accepts_marketing]', 'true');

      if (this.contact.phone) {
        body.append('contact[note]', `Telefone: ${this.contact.phone}`);
      }

      button.setAttribute('aria-busy', 'true');

      try {
        await fetch('/contact', { method: 'POST', body });
      } catch (error) {
        /* Falha de rede não bloqueia o prêmio: o cliente já cumpriu a etapa. */
      }

      button.removeAttribute('aria-busy');
      this.storage.set('email', this.contact.email);
      this.analytics.track('contact_submit', { phone: Boolean(this.contact.phone) });

      this.sendToWebhook();
    }

    /*
      Envia o lead para um webhook externo (ex.: n8n), em paralelo ao cadastro
      na Shopify. Usa FormData + no-cors: a requisição chega sempre, sem depender
      de CORS no servidor, e o n8n lê cada campo diretamente.
    */
    sendToWebhook() {
      const url = this.config.leadWebhook;
      if (!url) return;

      const lead = new FormData();
      lead.append('email', this.contact.email || '');
      lead.append('phone', this.contact.phone || '');
      lead.append('name', this.contact.name || '');
      lead.append('campaign', this.config.campaignId || '');
      lead.append('prize', this.prize ? this.prize.label : this.storage.get('prize') || '');
      lead.append('coupon', this.prize ? this.prize.coupon : this.storage.get('coupon') || '');
      lead.append('source', 'roleta');
      lead.append('page', location.href);

      fetch(url, { method: 'POST', mode: 'no-cors', body: lead }).catch(() => {
        /* Fire-and-forget: falha no webhook não pode travar a entrega do cupom. */
      });
    }

    /* ---------- Eventos ---------- */

    bindEvents() {
      this.querySelectorAll('[data-wheel-close]').forEach((element) => {
        element.addEventListener('click', () => this.close());
      });

      this.querySelector('[data-wheel-spin]').addEventListener('click', () => this.spin());

      this.querySelector('[data-wheel-claim]').addEventListener('click', () => {
        if (this.config.emailStep === 'after' && !this.storage.get('email')) {
          this.openContactForm();
        } else {
          this.showCoupon();
        }
      });

      this.querySelector('[data-wheel-form]').addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitContact(event.currentTarget);
      });

      const phoneInput = this.querySelector('[data-wheel-field="phone"] input');
      if (phoneInput) {
        phoneInput.addEventListener('input', () => {
          phoneInput.value = this.formatPhone(phoneInput.value);
        });
      }

      this.querySelector('[data-wheel-copy]').addEventListener('click', (event) => {
        const button = event.currentTarget;
        const code = this.querySelector('[data-wheel-code]').textContent;

        navigator.clipboard?.writeText(code);
        this.analytics.track('coupon_copy', { coupon: code });

        const hint = button.querySelector('.wheel__code-hint');
        const original = hint.textContent;
        hint.textContent = this.config.copiedText;
        setTimeout(() => {
          hint.textContent = original;
        }, 2000);
      });

      this.querySelector('[data-wheel-apply]').addEventListener('click', () => {
        // Copia o cupom antes de sair: se o desconto não aplicar sozinho,
        // o cliente já tem o código na área de transferência para colar.
        const code = this.querySelector('[data-wheel-code]').textContent;
        navigator.clipboard?.writeText(code);
        this.analytics.track('coupon_apply', { coupon: code });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.hidden) this.close();
      });

      // Redesenha ao girar o dispositivo, preservando a rotação atual.
      window.addEventListener('resize', () => {
        if (this.hidden || this.engine.spinning) return;
        this.renderer.draw();
      });

      // Qualquer elemento da página pode reabrir o pop-up.
      document.querySelectorAll('[data-wheel-open]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open('manual');
        });
      });
    }

    /* ---------- Abertura automática ---------- */

    shouldOpenAutomatically() {
      if (this.storage.has('claimed')) return false; // Já resgatou: nunca mais incomoda.
      if (this.storage.has('played')) return false; // Já girou.
      if (this.storage.has('closed')) return false; // Fechou no X: não insistir.
      if (this.config.showOnlyOnce && this.storage.has('appeared')) return false;
      return true;
    }

    scheduleAutoOpen() {
      // No editor de temas, abrir por timer trava a edição: cada ajuste recria
      // a seção e reinicia o cronômetro. Em vez disso, o pop-up abre só quando
      // a seção é selecionada e fecha ao selecionar outra.
      if (this.config.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) this.open('editor');
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) this.close();
        });
        return;
      }

      if (!this.shouldOpenAutomatically()) return;

      setTimeout(() => {
        if (this.isConnected) this.open('auto');
      }, this.config.apparitionDelay);
    }
  }

  if (!window.customElements.get('wheel-popup')) {
    window.customElements.define('wheel-popup', WheelPopup);
  }

  // Utilitário para testes: no console, `window.wheelReset()` limpa o histórico.
  window.wheelReset = () => {
    document.querySelectorAll('wheel-popup').forEach((popup) => popup.storage?.clear());
    console.info('Roleta: histórico limpo. Recarregue a página.');
  };
})();
